import type { FilterRuleEntity } from '@/shared/components/query-builder'
import type { ChangeEvent, FormEvent } from 'react'
import type { FieldViewProps, ValueState } from '../../types'
import type TextController from './Controller'

import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Operators } from '@/shared/components/query-builder'
import { isReferenceRule } from '@/shared/components/query-builder/utils/utils'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Radio } from '@/shared/components/ui/radio'

import { Group } from '../../components/InputGroup'

const NOOP = () => {}

type State = ValueState
type TextViewProps = FieldViewProps<TextController>

export default function TextView({
	storedValue,
	field,
	invalidMessage,
	isRemovable,
	isDirty,
	localValue,
	onRemove,
	onApply,
	onChange,
	closePopup,
}: TextViewProps) {
	const { t } = useTranslation()

	const focusTimeoutId = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	)
	const nextInputRef = useRef<HTMLInputElement | null>(null)
	const [state, setState] = useState<State>(storedValue)
	const focusNextInput = () => {
		const target = nextInputRef.current
		if (target) {
			focusTimeoutId.current = setTimeout(() => {
				target.focus()
			}, 10)
		}
	}
	useLayoutEffect(() => {
		focusNextInput()

		return () => clearTimeout(focusTimeoutId.current)
	})
	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (invalidMessage) return
		onApply?.()
		closePopup?.()
	}
	const onChangeCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
		const operation = event.target.value as Operators
		const isKeyboardEvent =
			(event.nativeEvent as PointerEvent).screenX === 0 &&
			(event.nativeEvent as PointerEvent).screenY === 0
		const callback = isKeyboardEvent ? NOOP : focusNextInput

		setState({ ...state, operation })
		callback()
		const value = [Operators.IS_EMPTY, Operators.IS_NOT_EMPTY].includes(
			operation
		)
			? '1'
			: state.value
		onChange({ operation, value })
	}
	const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
		const { operation } = state

		setState({ ...state, value: event.target.value })
		const value = [Operators.IS_EMPTY, Operators.IS_NOT_EMPTY].includes(
			operation as Operators
		)
			? '1'
			: event.target.value
		onChange({ operation, value })
	}
	const filterTypes = field.getFilterTypes(t)
	const { operation } = state
	const isInvalid = Boolean(invalidMessage)

	return (
		<Group
			onSubmit={handleSubmit}
			onRemove={isRemovable ? onRemove : undefined}
			isDisabledApply={
				!isDirty || Boolean(isInvalid || field.validate(localValue, t))
			}
		>
			<div className='flex max-w-[260px] min-w-[260px] flex-col gap-3 p-3'>
				{isReferenceRule(storedValue as FilterRuleEntity) && (
					<Alert variant='warning'>
						<InfoIcon />
						<AlertDescription className='text-xs'>
							{t('general_reference_rule_description', {
								defaultValue:
									'This filed now used as referance to another field.',
							})}
						</AlertDescription>
					</Alert>
				)}
				<div className='flex flex-col gap-1'>
					{filterTypes.map((m) => {
						const isCurrent = m.operation === operation

						return (
							<Fragment key={m.operation}>
								<div className='mt-2 flex items-center gap-2'>
									<Radio
										id={`radio-mode-${m.operation}}`}
										checked={isCurrent}
										name='mode'
										onChange={onChangeCheckbox}
										value={m.operation}
									/>
									<Label
										className='font-normal'
										htmlFor={`radio-mode-${m.operation}}`}
									>
										{m.label}
									</Label>
								</div>
								{isCurrent && m.hasInput ? (
									<>
										<Input
											className='w-full'
											key={m.operation}
											ref={nextInputRef}
											invalid={isInvalid}
											onChange={onChangeInput}
											value={state.value}
											autoComplete='off'
											autoCorrect='off'
										/>
										{invalidMessage && (
											<div className='text-sm text-destructive'>
												{invalidMessage}
											</div>
										)}
									</>
								) : null}
							</Fragment>
						)
					})}
				</div>
				{field.note && (
					<div className='text-sm text-muted-foreground'>{field.note}</div>
				)}
			</div>
		</Group>
	)
}
